import { __read, __spreadArray } from "tslib";
/**
 * implements morph animation with cubic splitting
 * @see http://thednp.github.io/kute.js/svgCubicMorph.html
 */

import { Cubic as CubicUtil } from '@antv/g-math';
import { vec3 } from 'gl-matrix';
import { Shape } from '../types';

function midPoint(a, b, t) {
  var ax = a[0];
  var ay = a[1];
  var bx = b[0];
  var by = b[1];
  return [ax + (bx - ax) * t, ay + (by - ay) * t];
}

function splitCubic(pts, t) {
  if (t === void 0) {
    t = 0.5;
  }

  var p0 = pts.slice(0, 2);
  var p1 = pts.slice(2, 4);
  var p2 = pts.slice(4, 6);
  var p3 = pts.slice(6, 8);
  var p4 = midPoint(p0, p1, t);
  var p5 = midPoint(p1, p2, t);
  var p6 = midPoint(p2, p3, t);
  var p7 = midPoint(p4, p5, t);
  var p8 = midPoint(p5, p6, t);
  var p9 = midPoint(p7, p8, t);
  return [// @ts-ignore
  ['C'].concat(p4, p7, p9), // @ts-ignore
  ['C'].concat(p8, p6, p3)];
}

function getCurveArray(segments) {
  return segments.map(function (segment, i, pathArray) {
    // @ts-ignore
    var segmentData = i && pathArray[i - 1].slice(-2).concat(segment.slice(1)); // @ts-ignore

    var curveLength = i ? CubicUtil.length.apply(CubicUtil, __spreadArray([], __read(segmentData), false)) : 0;
    var subsegs;

    if (i) {
      // must be [segment,segment]
      subsegs = curveLength ? splitCubic(segmentData) : [segment, segment];
    } else {
      subsegs = [segment];
    }

    return {
      s: segment,
      ss: subsegs,
      l: curveLength
    };
  });
}

export function equalizeSegments(path1, path2, TL) {
  var c1 = getCurveArray(path1);
  var c2 = getCurveArray(path2);
  var L1 = c1.length;
  var L2 = c2.length;
  var l1 = c1.filter(function (x) {
    return x.l;
  }).length;
  var l2 = c2.filter(function (x) {
    return x.l;
  }).length;
  var m1 = c1.filter(function (x) {
    return x.l;
  }).reduce(function (a, _a) {
    var l = _a.l;
    return a + l;
  }, 0) / l1 || 0;
  var m2 = c2.filter(function (x) {
    return x.l;
  }).reduce(function (a, _a) {
    var l = _a.l;
    return a + l;
  }, 0) / l2 || 0;
  var tl = TL || Math.max(L1, L2);
  var mm = [m1, m2];
  var dif = [tl - L1, tl - L2];
  var canSplit = 0;
  var result = [c1, c2].map(function (x, i) {
    // @ts-ignore
    return x.l === tl ? x.map(function (y) {
      return y.s;
    }) : x.map(function (y, j) {
      canSplit = j && dif[i] && y.l >= mm[i];
      dif[i] -= canSplit ? 1 : 0;
      return canSplit ? y.ss : [y.s];
    }).flat();
  });
  return result[0].length === result[1].length ? result : equalizeSegments(result[0], result[1], tl);
}
export function getDrawDirection(pathArray) {
  return getPathArea(pathArray) >= 0;
}

function getCubicSegArea(x0, y0, x1, y1, x2, y2, x3, y3) {
  // https://stackoverflow.com/a/15845996
  return 3 * ((y3 - y0) * (x1 + x2) - (x3 - x0) * (y1 + y2) + y1 * (x0 - x2) - x1 * (y0 - y2) + y3 * (x2 + x0 / 3) - x3 * (y2 + y0 / 3)) / 20;
}

export function getPathArea(pathArray) {
  var x = 0;
  var y = 0;
  var mx = 0;
  var my = 0;
  var len = 0;
  return pathArray.map(function (seg) {
    var _a;

    switch (seg[0]) {
      case 'M':
      case 'Z':
        mx = seg[0] === 'M' ? seg[1] : mx;
        my = seg[0] === 'M' ? seg[2] : my;
        x = mx;
        y = my;
        return 0;

      default:
        // @ts-ignore
        len = getCubicSegArea.apply(0, [x, y].concat(seg.slice(1)));
        _a = __read(seg.slice(-2), 2), x = _a[0], y = _a[1];
        return len;
    }
  }).reduce(function (a, b) {
    return a + b;
  }, 0);
} // reverse CURVE based pathArray segments only

export function reverseCurve(pathArray) {
  var rotatedCurve = pathArray.slice(1).map(function (x, i, curveOnly) {
    // @ts-ignore
    return !i ? pathArray[0].slice(1).concat(x.slice(1)) : curveOnly[i - 1].slice(-2).concat(x.slice(1));
  }) // @ts-ignore
  .map(function (x) {
    return x.map(function (y, i) {
      return x[x.length - i - 2 * (1 - i % 2)];
    });
  }).reverse(); // @ts-ignore

  return [['M'].concat(rotatedCurve[0].slice(0, 2))].concat(rotatedCurve.map(function (x) {
    return ['C'].concat(x.slice(2));
  }));
}
export function clonePath(pathArray) {
  // @ts-ignore
  return pathArray.map(function (x) {
    if (Array.isArray(x)) {
      // @ts-ignore
      return clonePath(x);
    }

    return !Number.isNaN(+x) ? +x : x;
  });
}

function getRotations(a) {
  var segCount = a.length;
  var pointCount = segCount - 1;
  return a.map(function (f, idx) {
    return a.map(function (p, i) {
      var oldSegIdx = idx + i;
      var seg;

      if (i === 0 || a[oldSegIdx] && a[oldSegIdx][0] === 'M') {
        seg = a[oldSegIdx]; // @ts-ignore

        return ['M'].concat(seg.slice(-2));
      }

      if (oldSegIdx >= segCount) oldSegIdx -= pointCount;
      return a[oldSegIdx];
    });
  });
}

function distanceSquareRoot(a, b) {
  return Math.sqrt((a[0] - b[0]) * (a[0] - b[0]) + (a[1] - b[1]) * (a[1] - b[1]));
}

export function getRotatedCurve(a, b) {
  var segCount = a.length - 1;
  var lineLengths = [];
  var computedIndex = 0;
  var sumLensSqrd = 0;
  var rotations = getRotations(a);
  rotations.forEach(function (r, i) {
    a.slice(1).forEach(function (s, j) {
      // @ts-ignore
      sumLensSqrd += distanceSquareRoot(a[(i + j) % segCount].slice(-2), b[j % segCount].slice(-2));
    });
    lineLengths[i] = sumLensSqrd;
    sumLensSqrd = 0;
  });
  computedIndex = lineLengths.indexOf(Math.min.apply(null, lineLengths));
  return rotations[computedIndex];
}

function commandsToPathString(commands, localTransform, anchor, parsedStyle) {
  var x = parsedStyle.x,
      y = parsedStyle.y;
  return commands.reduce(function (prev, cur) {
    var path = '';

    if (cur[0] === 'M' || cur[0] === 'L') {
      var p = vec3.fromValues(cur[1] - x, cur[2] - y, 0);
      vec3.transformMat4(p, p, localTransform);
      path = "".concat(cur[0]).concat(p[0], ",").concat(p[1]);
    } else if (cur[0] === 'Z') {
      path = cur[0];
    } else if (cur[0] === 'C') {
      var p1 = vec3.fromValues(cur[1] - x, cur[2] - y, 0);
      var p2 = vec3.fromValues(cur[3] - x, cur[4] - y, 0);
      var p3 = vec3.fromValues(cur[5] - x, cur[6] - y, 0);
      vec3.transformMat4(p1, p1, localTransform);
      vec3.transformMat4(p2, p2, localTransform);
      vec3.transformMat4(p3, p3, localTransform);
      path = "".concat(cur[0]).concat(p1[0], ",").concat(p1[1], ",").concat(p2[0], ",").concat(p2[1], ",").concat(p3[0], ",").concat(p3[1]);
    } else if (cur[0] === 'A') {
      var c = vec3.fromValues(cur[6] - x, cur[7] - y, 0);
      vec3.transformMat4(c, c, localTransform);
      path = "".concat(cur[0]).concat(cur[1], ",").concat(cur[2], ",").concat(cur[3], ",").concat(cur[4], ",").concat(cur[5], ",").concat(c[0], ",").concat(c[1]);
    }

    return prev += path;
  }, '');
}

function lineToCommands(x1, y1, x2, y2) {
  return [['M', x1, y1], ['L', x2, y2]];
}

function ellipseToCommands(rx, ry, cx, cy) {
  var factor = (-1 + Math.sqrt(2)) / 3 * 4;
  var dx = rx * factor;
  var dy = ry * factor;
  var left = cx - rx;
  var right = cx + rx;
  var top = cy - ry;
  var bottom = cy + ry;
  return [['M', left, cy], ['C', left, cy - dy, cx - dx, top, cx, top], ['C', cx + dx, top, right, cy - dy, right, cy], ['C', right, cy + dy, cx + dx, bottom, cx, bottom], ['C', cx - dx, bottom, left, cy + dy, left, cy], ['Z']];
}

function polygonToCommands(points) {
  return points.map(function (point, i) {
    return [i === 0 ? 'M' : 'L', point[0], point[1]];
  });
}

function rectToCommands(width, height, x, y, radius) {
  // @see https://gist.github.com/danielpquinn/dd966af424030d47e476
  if (radius) {
    return [['M', x, radius + y], ['A', radius, radius, 0, 0, 1, radius + x, y], ['L', width - radius + x, y], ['A', radius, radius, 0, 0, 1, width + x, radius + y], ['L', width + x, height - radius + y], ['A', radius, radius, 0, 0, 1, width - radius + x, height + y], ['L', radius + x, height + y], ['A', radius, radius, 0, 0, 1, x, height - radius + y], ['Z']];
  }

  return [['M', x, y], ['L', x + width, y], ['L', x + width, y + height], ['L', x, y + height], ['Z']];
}
/**
 * convert object to path, should account for:
 * * transform & origin
 * * anchor
 * * lineWidth
 */


export function convertToPath(object) {
  var localTransform = object.getLocalTransform();
  var anchor = object.style.anchor;
  var commands = [];

  switch (object.nodeName) {
    case Shape.LINE:
      var _a = object.parsedStyle,
          x1 = _a.x1,
          y1 = _a.y1,
          x2 = _a.x2,
          y2 = _a.y2;
      commands = lineToCommands(x1, y1, x2, y2);
      break;

    case Shape.CIRCLE:
      {
        var _b = object.parsedStyle,
            r = _b.rInPixels,
            _c = _b.x,
            x_1 = _c === void 0 ? 0 : _c,
            _d = _b.y,
            y_1 = _d === void 0 ? 0 : _d;
        commands = ellipseToCommands(r, r, x_1, y_1);
        break;
      }

    case Shape.ELLIPSE:
      {
        var _e = object.parsedStyle,
            rx = _e.rxInPixels,
            ry = _e.ryInPixels,
            _f = _e.x,
            x_2 = _f === void 0 ? 0 : _f,
            _g = _e.y,
            y_2 = _g === void 0 ? 0 : _g;
        commands = ellipseToCommands(rx, ry, x_2, y_2);
        break;
      }

    case Shape.POLYLINE:
    case Shape.POLYGON:
      var points = object.parsedStyle.points;
      commands = polygonToCommands(points.points);
      break;

    case Shape.RECT:
      var _h = object.parsedStyle,
          width = _h.widthInPixels,
          height = _h.heightInPixels,
          x = _h.x,
          y = _h.y,
          radius = _h.radius;
      commands = rectToCommands(width, height, x, y, radius);
      break;

    case Shape.PATH:
      commands = object.parsedStyle.path.curve;
      break;
  }

  if (commands.length) {
    return commandsToPathString(commands, localTransform, anchor, object.parsedStyle);
  }
}